import React, { useState, useRef, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { FaVolumeMute, FaVolumeUp, FaArrowRight, FaArrowLeft } from "react-icons/fa";

const WhatStudentSay = () => {
  // Animations
  const fadeInLeft = {
    hidden: { opacity: 0, x: -30 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.4, ease: "easeOut" } },
  };
  const fadeInRight = {
    hidden: { opacity: 0, x: 30 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.4, ease: "easeOut" } },
  };

  // State & refs
  const [isMuted, setIsMuted] = useState(true);
  const [videoIndex, setVideoIndex] = useState(0);
  const videoRef = useRef(null);
  const iframeRef = useRef(null);

  // Player.js instance + readiness for the iframe
  const playerRef = useRef(null);
  const playerReadyRef = useRef(false);

  // --- Load Gumlet Player.js once (correct CDN path) ---
  const ensurePlayerJs = () => {
    if (typeof window === "undefined") return Promise.resolve();
    // cache the promise on window to avoid multiple injections
    if (window.__gumletPlayerJsPromise) return window.__gumletPlayerJsPromise;
    window.__gumletPlayerJsPromise = new Promise((resolve) => {
      if (window.playerjs) return resolve();
      const s = document.createElement("script");
      s.src = "https://cdn.jsdelivr.net/npm/@gumlet/player.js/dist/player.min.js";
      s.async = true;
      s.onload = resolve;
      document.head.appendChild(s);
    });
    return window.__gumletPlayerJsPromise;
  };

  // Sources
  const rawVideoSources = [
    {
      type: "iframe",
      src: "https://play.gumlet.io/embed/69525f71aa03be0b83537204?background=false&autoplay=true&loop=true&disableControls=false&muted=true&disable_captions=true&disable_player_controls=settings&preload=none",
      title: "Gumlet Embed",
    },
    {
      type: "iframe",
      src: "https://play.gumlet.io/embed/69525f714c8a18286b08e272?background=false&autoplay=true&loop=true&disableControls=false&muted=true&disable_captions=true&disable_player_controls=settings&preload=none",
      title: "Gumlet Embed",
    },
    {
      type: "iframe",
      src: "https://play.gumlet.io/embed/68d641bd028ee7562200e520?background=false&autoplay=true&loop=true&disableControls=false&muted=true&disable_captions=true&disable_player_controls=settings&preload=none",
      title: "Gumlet Embed",
    },
    {
      type: "iframe",
      src: "https://play.gumlet.io/embed/68d641a2028ee7562200e35b?background=false&autoplay=true&loop=true&disableControls=false&muted=true&disable_captions=true&disable_player_controls=settings&preload=none",
      title: "Gumlet Embed",
    },
    {
      type: "iframe",
      src: "https://play.gumlet.io/embed/68cb9b20b0773038fc661aa2?background=false&autoplay=true&loop=true&disableControls=false&muted=true&disable_captions=true&disable_player_controls=settings&preload=none",
      title: "Gumlet Embed",
    },
    {
      type: "iframe",
      src: "https://play.gumlet.io/embed/68d65d6d29d13fb683d03776?background=false&autoplay=true&loop=true&disableControls=false&muted=true&disable_captions=true&disable_player_controls=settings&preload=none",
      title: "Gumlet Embed",
    }
  ];

  const videoSources = useMemo(() => rawVideoSources, []);

  // Initialize Player.js when the iframe source is active
  useEffect(() => {
    playerRef.current = null;
    playerReadyRef.current = false;

    const current = videoSources[videoIndex];
    if (current.type !== "iframe") return;

    let canceled = false;

    ensurePlayerJs().then(() => {
      if (canceled || !iframeRef.current || !window.playerjs) return;
      const p = new window.playerjs.Player(iframeRef.current);
      playerRef.current = p;

      p.on("ready", () => {
        if (canceled) return;
        playerReadyRef.current = true;

        // Apply current mute state and ensure playback
        const can = (m) => (p.supports ? p.supports("method", m) : typeof p[m] === "function");

        if (isMuted) {
          if (can("mute")) p.mute();
        } else {
          if (can("unmute")) p.unmute();
          if (can("setVolume")) p.setVolume(100);
        }
        if (can("play")) p.play();
      });
    });

    return () => {
      canceled = true;
      playerRef.current = null;
      playerReadyRef.current = false;
    };
  }, [videoIndex, videoSources, isMuted]);

  // Keep players in sync with isMuted
  useEffect(() => {
    const current = videoSources[videoIndex];

    if (current.type === "video" && videoRef.current) {
      videoRef.current.muted = isMuted;
      if (!isMuted) {
        // user may have clicked already; try to play with sound
        videoRef.current.play?.().catch(() => { });
      }
    }

    if (current.type === "iframe" && playerRef.current && playerReadyRef.current) {
      const p = playerRef.current;
      const can = (m) => (p.supports ? p.supports("method", m) : typeof p[m] === "function");

      if (isMuted) {
        if (can("mute")) p.mute();
      } else {
        if (can("unmute")) p.unmute();
        if (can("setVolume")) p.setVolume(100);
        if (can("play")) p.play();
      }
    }
  }, [isMuted, videoIndex, videoSources]);

  // Toggle mute (works for both)
  const toggleMute = () => {
    const current = videoSources[videoIndex];

    if (current.type === "video") {
      const el = videoRef.current;
      if (!el) return;
      const next = !el.muted;
      el.muted = next;
      setIsMuted(next);
      if (!next) el.play?.().catch(() => { });
      return;
    }

    // iframe branch
    const p = playerRef.current;
    const next = !isMuted;

    if (p && playerReadyRef.current) {
      const can = (m) => (p.supports ? p.supports("method", m) : typeof p[m] === "function");

      if (!next) {
        if (can("unmute")) p.unmute();;
        if (can("setVolume")) p.setVolume(100);
        if (can("play")) p.play();
      } else {
        if (can("mute")) p.mute();
      }
    }

    setIsMuted(next);
  };

  // Slider navigation
  const handleNext = () => {
    setVideoIndex((prev) => (prev + 1) % videoSources.length);
    setIsMuted(true); // start muted on new slide (autoplay policy)
  };
  const handlePrev = () => {
    setVideoIndex((prev) => (prev - 1 + videoSources.length) % videoSources.length);
    setIsMuted(true);
  };

  return (
    <div className="lg:p- bg-gray-100 flex items-center justify-center">
      <motion.section className="instructor-section my-5">
        <div className="p-4 container flex flex-col lg:flex-row items-center lg:space-x-12 space-y-8 lg:space-y-0">
          {/* Left side */}
          <motion.div
            className="flex-1 lg:p-4 lg:text-left"
            initial="hidden"
            whileInView="visible"
            variants={fadeInRight}
            viewport={{ once: true, margin: "-80px" }}
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#140342]">
              Hear From Our <span className="predict-highlight">Students</span>
            </h2>
            <p className="section-description mt-4 font-bold text-[#4F547B]">
              Dive into the journeys of NEET aspirants who turned their dreams
              into reality not just through hard work, but with the right
              guidance at the right time.
            </p>
            <p className="section-description mt- font-bold text-[#4F547B]">
              Hear directly from students, just like you, who faced the same
              confusion and stress but found clarity and confidence with NEET
              Support.
              <br />
              See how we’ve helped students navigate the complex NEET
              counselling process—from understanding eligibility and choosing
              the right colleges to avoiding common mistakes and securing their
              dream medical seats.
            </p>
          </motion.div>

          {/* Right side */}
          <motion.div
            className="flex-1"
            initial="hidden"
            whileInView="visible"
            variants={fadeInLeft}
            viewport={{ once: true }}
          >
            <div className="flex items-center justify-center gap-3 sm:gap-4 md:gap-6">
              {/* Left Arrow */}
              <button
                type="button"
                onClick={handlePrev}
                aria-label="Previous video"
                className="shrink-0 bg-white text-[#ff9245] p-2 rounded-full focus:outline-none focus:ring-2 focus:ring-[#ff9245]"
                title="Previous"
              >
                <FaArrowLeft size={22} />
              </button>

              {/* Player area */}
              <style>{`
                @media (min-width: 768px) and (max-width: 1023px) {
                  .video-player-responsive { max-width: 40vw !important; }
                }
                @media (min-width: 1024px) {
                  .video-player-responsive { max-width: 20vw !important; }
                }
              `}</style>

              <div
                key={`iframe-${videoIndex}`}
                className="video-player-responsive relative flex-1 min-w-0 rounded-lg shadow-lg w-[200px] md:w-[250px] lg:w-[300px] xl:w-[340px] min-h-[300px] md:min-h-0 h-full"
                style={{ aspectRatio: "9/16", maxWidth: "100vw" }}
              >
                <iframe
                  ref={iframeRef}
                  loading="lazy"
                  src={videoSources[videoIndex].src}
                  style={{
                    border: "none",
                    position: "absolute",
                    borderRadius: "8px",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    zIndex: 0,
                  }}
                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
                  title={rawVideoSources[videoIndex].title || "Gumlet video"}
                />

                {/* ALWAYS-VISIBLE Mute/Unmute overlay (controls iframe via Player.js) */}
                <button
                  type="button"
                  onClick={toggleMute}
                  aria-label={isMuted ? "Unmute audio" : "Mute audio"}
                  className="absolute top-3 left-3 bg-black/60 p-2 rounded-full text-white focus:outline-none focus:ring-2 focus:ring-white/60 z-10"
                  title={isMuted ? "Unmute" : "Mute"}
                >
                  {isMuted ? <FaVolumeMute size={20} /> : <FaVolumeUp size={20} />}
                </button>
              </div>

              {/* Right Arrow */}
              <button
                type="button"
                onClick={handleNext}
                aria-label="Next video"
                className="shrink-0 bg-white text-[#ff9245] p-2 rounded-full focus:outline-none focus:ring-2 focus:ring-[#ff9245]"
                title="Next"
              >
                <FaArrowRight size={22} />
              </button>
            </div>

            {/* Video Navigation Dots */}
            <div className="flex justify-center gap-2 mt-4">
              {videoSources.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setVideoIndex(index);
                    setIsMuted(true);
                  }}
                  className={`w-3 h-3 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#ff9245] ${index === videoIndex
                      ? 'bg-[#ff9245] scale-110'
                      : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                  aria-label={`Go to video ${index + 1}`}
                  title={`Video ${index + 1}`}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
};

export default WhatStudentSay;
